import {
  Anchor,
  Bell,
  Bike,
  Building2,
  Calendar,
  CreditCard,
  FileText,
  Gift,
  Inbox,
  MapPin,
  Search,
  Ticket,
  Users,
} from "lucide-react";
import * as React from "react";

import { strings } from "@/I18n";
import { cn } from "@/lib/utils";

export type EmptyStateType =
  | "bikes"
  | "users"
  | "transactions"
  | "stations"
  | "docks"
  | "rentals"
  | "cities"
  | "coupons"
  | "referrals"
  | "notifications"
  | "reservations"
  | "search"
  | "generic"
  | "empty";

const emptyStateConfig: Record<
  EmptyStateType,
  { icon: React.ReactNode; messageKey: string }
> = {
  bikes: {
    icon: <Bike className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noBikes",
  },
  users: {
    icon: <Users className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noUsersFound",
  },
  transactions: {
    icon: <CreditCard className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noTransactionsFound",
  },
  stations: {
    icon: <MapPin className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noStations",
  },
  docks: {
    icon: <Anchor className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noDocks",
  },
  rentals: {
    icon: <Calendar className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noRentals",
  },
  cities: {
    icon: <Building2 className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noCitiesFound",
  },
  coupons: {
    icon: <Ticket className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noCouponsFound",
  },
  referrals: {
    icon: <Gift className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noReferralsFound",
  },
  notifications: {
    icon: <Bell className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noNotificationsFound",
  },
  reservations: {
    icon: <FileText className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noReservations",
  },
  search: {
    icon: <Search className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noData",
  },
  generic: {
    icon: <Inbox className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noDataAvailable",
  },
  empty: {
    icon: <Inbox className="tw-h-12 tw-w-12" />,
    messageKey: "emptyStates.noData",
  },
};

export interface EmptyStateProps {
  /** The type of empty state - determines icon and default message */
  type?: EmptyStateType;
  /** Custom message (overrides default) */
  message?: string;
  /** Optional description text below the message */
  description?: string;
  /** Custom icon (overrides type-based icon) */
  icon?: React.ReactNode;
  /** Optional action button/link */
  action?: React.ReactNode;
  className?: string;
}

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    { type = "generic", message, description, icon, action, className },
    ref
  ) => {
    const config = emptyStateConfig[type];
    const displayIcon = icon ?? config.icon;
    const displayMessage = message ?? strings(config.messageKey);

    return (
      <div
        ref={ref}
        className={cn(
          "tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-px-4 tw-text-center tw-min-h-[300px]",
          className
        )}
      >
        {displayIcon && (
          <div className="tw-mb-4 tw-text-muted-foreground/50">
            {displayIcon}
          </div>
        )}
        <h3 className="tw-text-lg tw-font-medium tw-text-foreground tw-mb-1">
          {displayMessage}
        </h3>
        {description && (
          <p className="tw-text-sm tw-text-muted-foreground tw-max-w-md">
            {description}
          </p>
        )}
        {action && <div className="tw-mt-6">{action}</div>}
      </div>
    );
  }
);

EmptyState.displayName = "EmptyState";

export { EmptyState };
